import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Check, RefreshCw, Scissors } from 'lucide-react';
import { autoCropWhiteMargins } from '../../utils/imageCropping';
import { useClickOutside } from '../../hooks/useClickOutside';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useNotifications } from '../../contexts/NotificationContext';
import { captureDiagram } from '../../utils/diagramCapture';
import { useReactFlow } from 'reactflow';

interface CropDiagramDialogProps {
  open: boolean;
  onClose: () => void;
  onCropComplete: (croppedImageBase64: string) => void;
  initialImage?: string;
  // The 'open' prop is no longer needed as visibility is controlled by AnimatePresence in the parent
}

interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface HandlePosition {
  x: number;
  y: number;
  cursor: string;
  position: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'right' | 'bottom' | 'left';
  isCorner?: boolean;
}

export default function CropDiagramDialog({
  onClose,
  onCropComplete,
  initialImage
}: CropDiagramDialogProps) {
  // const [isVisible, setIsVisible] = useState(true); // Removed, parent controls visibility

  // Simplified close handler
  const handleClose = () => {
    onClose();
  };

  // State for image and cropping
  const [imageSource, setImageSource] = useState<string | undefined>(initialImage);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cropRect, setCropRect] = useState<CropRect | null>(null);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [activeHandle, setActiveHandle] = useState<string | null>(null);
  const [hoverHandle, setHoverHandle] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  
  // Refs
  const dialogRef = useClickOutside<HTMLDivElement>(handleClose);
  useEscapeKey(handleClose); // Use escape key to close the dialog
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Hooks
  const { showNotification } = useNotifications();
  const { fitView } = useReactFlow();
  
  // Capture diagram from React Flow
  const captureReactFlowDiagram = useCallback(async () => {
    setIsCapturing(true);
    try {
      // Fit the view before capturing
      fitView();
      // Capture the diagram using the utility function
      const dataUrl = await captureDiagram(); // Call without arguments
      
      // Set the captured image as the source
      setImageSource(dataUrl);
      setCropRect(null); // Reset crop rectangle when capturing a new image
      showNotification('success', 'Diagram captured successfully in light mode for optimal printing');
    } catch (error) {
      console.error('Error capturing diagram:', error);
      showNotification('error', 'Failed to capture diagram');
    } finally {
      setIsCapturing(false);
    }
  }, [showNotification, fitView]);
  
  // Calculate handle positions based on the crop rectangle
  const handlePositions = useMemo<HandlePosition[]>(() => {
    if (!cropRect) return [];
    
    // Corner handles
    const cornerHandles: HandlePosition[] = [
      { x: cropRect.x, y: cropRect.y, cursor: 'nwse-resize', position: 'topLeft', isCorner: true },
      { x: cropRect.x + cropRect.width, y: cropRect.y, cursor: 'nesw-resize', position: 'topRight', isCorner: true },
      { x: cropRect.x, y: cropRect.y + cropRect.height, cursor: 'nesw-resize', position: 'bottomLeft', isCorner: true },
      { x: cropRect.x + cropRect.width, y: cropRect.y + cropRect.height, cursor: 'nwse-resize', position: 'bottomRight', isCorner: true }
    ];
    
    // Edge handles (middle of each edge)
    const edgeHandles: HandlePosition[] = [
      { x: cropRect.x + cropRect.width / 2, y: cropRect.y, cursor: 'ns-resize', position: 'top', isCorner: false },
      { x: cropRect.x + cropRect.width, y: cropRect.y + cropRect.height / 2, cursor: 'ew-resize', position: 'right', isCorner: false },
      { x: cropRect.x + cropRect.width / 2, y: cropRect.y + cropRect.height, cursor: 'ns-resize', position: 'bottom', isCorner: false },
      { x: cropRect.x, y: cropRect.y + cropRect.height / 2, cursor: 'ew-resize', position: 'left', isCorner: false }
    ];
    
    return [...cornerHandles, ...edgeHandles];
  }, [cropRect]);

  // Initialize the image size when the image is loaded
  useEffect(() => {
    if (imageSource && imageRef.current) {
      const img = imageRef.current;
      
      const updateImageSize = () => {
        setImageSize({
          width: img.width,
          height: img.height
        });
      };
      
      if (img.complete) {
        updateImageSize();
      } else {
        img.onload = updateImageSize;
      }
    }
  }, [imageSource]);

  // Initialize default crop rectangle when image is loaded
  useEffect(() => {
    if (imageSource && imageSize && canvasRef.current && !cropRect) {
      const canvas = canvasRef.current;
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      
      // Calculate a default crop rectangle that's centered and covers most of the image
      // with a 3:2 aspect ratio (middle of our allowed range)
      const aspectRatio = 3/2;
      
      let width, height;
      
      // Determine if we should constrain by width or height
      if (canvasWidth / canvasHeight > aspectRatio) {
        // Canvas is wider than our target aspect ratio
        height = canvasHeight * 0.8;
        width = height * aspectRatio;
      } else {
        // Canvas is taller than our target aspect ratio
        width = canvasWidth * 0.8;
        height = width / aspectRatio;
      }
      
      // Center the crop rectangle
      const x = (canvasWidth - width) / 2;
      const y = (canvasHeight - height) / 2;
      
      setCropRect({
        x,
        y,
        width,
        height
      });
    }
  }, [imageSource, imageSize, cropRect]);

  // Initialize the canvas when the image is loaded
  useEffect(() => {
    if (imageSource && imageRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        // Clear the canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Draw the semi-transparent overlay
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // If there's a crop rectangle, clear that area to show the image underneath
        if (cropRect) {
          ctx.clearRect(cropRect.x, cropRect.y, cropRect.width, cropRect.height);
          
          // Draw a border around the crop rectangle
          ctx.strokeStyle = '#2d3282'; // Primary blue color
          ctx.lineWidth = 3;
          ctx.strokeRect(cropRect.x, cropRect.y, cropRect.width, cropRect.height);
          
          // Draw handles at the corners and edges
          const handleSize = 10; // Smaller square handles
          ctx.fillStyle = '#2d3282';
          
          handlePositions.forEach(handle => {
            ctx.beginPath();
            // Draw square handles instead of circles
            ctx.rect(
              handle.x - handleSize / 2, 
              handle.y - handleSize / 2, 
              handleSize, 
              handleSize
            );
            ctx.fill();
          });
        }
      }
    }
  }, [imageSource, cropRect, handlePositions]);
  
  // Get scaled mouse coordinates relative to the canvas
  const getScaledMouseCoordinates = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate the scaling factors
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Apply scaling to get the correct coordinates in canvas space
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    return { x, y };
  }, []);
  
  // Check if a point is near a handle
  const getHandleAtPosition = useCallback((x: number, y: number): string | null => {
    if (!handlePositions.length) return null;
    
    const handleSize = 24; // Larger detection area for easier interaction
    
    for (const handle of handlePositions) {
      const distance = Math.sqrt(
        Math.pow(handle.x - x, 2) + Math.pow(handle.y - y, 2)
      );
      
      if (distance <= handleSize / 2) {
        return handle.position;
      }
    }
    
    return null;
  }, [handlePositions]);

  // Check if a point is inside the crop rectangle
  const isPointInCropRect = useCallback((x: number, y: number): boolean => {
    if (!cropRect) return false;
    
    return (
      x >= cropRect.x &&
      x <= cropRect.x + cropRect.width &&
      y >= cropRect.y &&
      y <= cropRect.y + cropRect.height
    );
  }, [cropRect]);

  // Handle mouse down to start dragging or resizing
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    
    // Prevent default browser behavior to avoid image selection
    e.preventDefault();
    
    // Get scaled mouse coordinates
    const { x, y } = getScaledMouseCoordinates(e);
    
    // Set the start point for all interactions
    setStartPoint({ x, y });
    
    if (cropRect) {
      // Check if we're on a handle
      const handle = getHandleAtPosition(x, y);
      
      if (handle) {
        // We're on a handle, so we're resizing
        setActiveHandle(handle);
        setIsDragging(true);
      } else if (isPointInCropRect(x, y)) {
        // We're inside the crop rect, so we're moving it
        setActiveHandle('move');
        setIsDragging(true);
      } else {
        // We're outside, so we're creating a new crop rect
        setActiveHandle(null);
        setIsDragging(true);
      }
    } else {
      // No crop rect exists, so we're creating one
      setActiveHandle(null);
      setIsDragging(true);
    }
  }, [cropRect, getHandleAtPosition, isPointInCropRect, getScaledMouseCoordinates]);
  
  // Enforce aspect ratio constraints
  const enforceAspectRatioConstraints = useCallback((newWidth: number, newHeight: number): { width: number, height: number } => {
    const aspectRatio = newWidth / newHeight;
    
    // Allowed aspect ratio range: 4:1 (4.0) to 4:3 (1.33)
    const minAspectRatio = 4/3; // 1.33
    const maxAspectRatio = 4/1; // 4.0
    
    if (aspectRatio < minAspectRatio) {
      // Too tall, adjust height
      return {
        width: newWidth,
        height: newWidth / minAspectRatio
      };
    } else if (aspectRatio > maxAspectRatio) {
      // Too wide, adjust width
      return {
        width: newHeight * maxAspectRatio,
        height: newHeight
      };
    }
    
    // Within constraints
    return { width: newWidth, height: newHeight };
  }, []);

  // Helper function to get the appropriate cursor for a handle
  const getCursorForHandle = useCallback((handle: string): string => {
    switch (handle) {
      case 'move':
        return 'move';
      case 'topLeft':
      case 'bottomRight':
        return 'nwse-resize';
      case 'topRight':
      case 'bottomLeft':
        return 'nesw-resize';
      case 'top':
      case 'bottom':
        return 'ns-resize';
      case 'left':
      case 'right':
        return 'ew-resize';
      default:
        return 'crosshair';
    }
  }, []);

  // Handle mouse move to update the crop rectangle
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // Prevent default browser behavior to avoid image selection
    e.preventDefault();
    
    if (!canvasRef.current) return;
    
    // Get scaled mouse coordinates for hover detection
    const coords = getScaledMouseCoordinates(e);
    
    // Check if we're hovering over a handle
    if (cropRect) {
      const handle = getHandleAtPosition(coords.x, coords.y);
      setHoverHandle(handle);
    }
    
    if (!isDragging || !startPoint) return;
    
    const canvas = canvasRef.current;
    
    // Ensure x and y are within canvas bounds
    const boundedX = Math.max(0, Math.min(canvas.width, coords.x));
    const boundedY = Math.max(0, Math.min(canvas.height, coords.y));
    
    if (activeHandle === null && !cropRect) {
      // Creating a new crop rectangle
      const width = Math.abs(boundedX - startPoint.x);
      const height = Math.abs(boundedY - startPoint.y);
      const left = Math.min(startPoint.x, boundedX);
      const top = Math.min(startPoint.y, boundedY);
      
      // Enforce aspect ratio constraints
      const { width: constrainedWidth, height: constrainedHeight } = enforceAspectRatioConstraints(width, height);
      
      setCropRect({
        x: left,
        y: top,
        width: constrainedWidth,
        height: constrainedHeight
      });
    } else if (cropRect) {
      if (activeHandle === 'move') {
        // Moving the entire crop rectangle
        const deltaX = boundedX - startPoint.x;
        const deltaY = boundedY - startPoint.y;
        
        // Calculate new position ensuring it stays within canvas
        let newX = cropRect.x + deltaX;
        let newY = cropRect.y + deltaY;
        
        // Ensure the crop rectangle stays within the canvas
        newX = Math.max(0, Math.min(canvas.width - cropRect.width, newX));
        newY = Math.max(0, Math.min(canvas.height - cropRect.height, newY));
        
        setCropRect({
          ...cropRect,
          x: newX,
          y: newY
        });
        
        // Update start point for smooth dragging
        setStartPoint({ x: boundedX, y: boundedY });
      } else if (activeHandle) {
        // Resizing using a handle
        let newWidth = cropRect.width;
        let newHeight = cropRect.height;
        let newX = cropRect.x;
        let newY = cropRect.y;
        
        switch (activeHandle) {
          case 'topLeft':
            newWidth = cropRect.x + cropRect.width - boundedX;
            newHeight = cropRect.y + cropRect.height - boundedY;
            newX = boundedX;
            newY = boundedY;
            break;
          case 'topRight':
            newWidth = boundedX - cropRect.x;
            newHeight = cropRect.y + cropRect.height - boundedY;
            newY = boundedY;
            break;
          case 'bottomLeft':
            newWidth = cropRect.x + cropRect.width - boundedX;
            newHeight = boundedY - cropRect.y;
            newX = boundedX;
            break;
          case 'bottomRight':
            newWidth = boundedX - cropRect.x;
            newHeight = boundedY - cropRect.y;
            break;
          // Edge handles
          case 'top':
            newHeight = cropRect.y + cropRect.height - boundedY;
            newY = boundedY;
            break;
          case 'right':
            newWidth = boundedX - cropRect.x;
            break;
          case 'bottom':
            newHeight = boundedY - cropRect.y;
            break;
          case 'left':
            newWidth = cropRect.x + cropRect.width - boundedX;
            newX = boundedX;
            break;
        }
        
        // Ensure minimum size
        newWidth = Math.max(20, newWidth);
        newHeight = Math.max(20, newHeight);
        
        // Enforce aspect ratio constraints
        let { width: constrainedWidth, height: constrainedHeight } = enforceAspectRatioConstraints(newWidth, newHeight);
        
        // Apply the constrained dimensions while maintaining the anchor point
        switch (activeHandle) {
          case 'topLeft':
            newX = cropRect.x + cropRect.width - constrainedWidth;
            newY = cropRect.y + cropRect.height - constrainedHeight;
            break;
          case 'topRight':
            newY = cropRect.y + cropRect.height - constrainedHeight;
            break;
          case 'bottomLeft':
            newX = cropRect.x + cropRect.width - constrainedWidth;
            break;
          case 'top':
            newY = cropRect.y + cropRect.height - constrainedHeight;
            break;
          case 'left':
            newX = cropRect.x + cropRect.width - constrainedWidth;
            break;
        }
        
        // Ensure the crop rectangle stays within the canvas
        if (newX < 0) {
          newX = 0;
          constrainedWidth = cropRect.x + cropRect.width;
        }
        if (newY < 0) {
          newY = 0;
          constrainedHeight = cropRect.y + cropRect.height;
        }
        if (newX + constrainedWidth > canvas.width) {
          constrainedWidth = canvas.width - newX;
        }
        if (newY + constrainedHeight > canvas.height) {
          constrainedHeight = canvas.height - newY;
        }
        
        setCropRect({
          x: newX,
          y: newY,
          width: constrainedWidth,
          height: constrainedHeight
        });
      }
    }
  }, [isDragging, startPoint, cropRect, activeHandle, enforceAspectRatioConstraints, getScaledMouseCoordinates]);
  
  // Handle mouse up to finalize the crop rectangle
  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // Prevent default browser behavior to avoid image selection
    e.preventDefault();
    
    setIsDragging(false);
    setActiveHandle(null);
  }, []);
  
  // Create the cropped image
  const createCroppedImage = useCallback(async () => {
    if (!imageSource || !cropRect || !imageRef.current) {
      showNotification('error', 'Please draw a rectangle to crop the diagram');
      return;
    }
    
    try {
      // Create a new image element
      const image = new Image();
      image.src = imageSource;
      
      // Wait for the image to load
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
      });
      
      // Get the actual image dimensions
      const actualImageWidth = image.naturalWidth;
      const actualImageHeight = image.naturalHeight;
      
      // Calculate the scaling factors between the canvas and the actual image
      const canvasElement = canvasRef.current;
      if (!canvasElement) {
        throw new Error('Canvas element not found');
      }
      
      
      // Get the canvas dimensions
      const canvasWidth = canvasElement.width;
      const canvasHeight = canvasElement.height;
      
      // Calculate the displayed image dimensions within the container
      // (accounting for object-contain scaling)
      let displayedWidth, displayedHeight, offsetX, offsetY;
      
      const canvasRatio = canvasWidth / canvasHeight;
      const imageRatio = actualImageWidth / actualImageHeight;
      
      if (imageRatio > canvasRatio) {
        // Image is wider than canvas (relative to their heights)
        displayedWidth = canvasWidth;
        displayedHeight = displayedWidth / imageRatio;
        offsetX = 0;
        offsetY = (canvasHeight - displayedHeight) / 2;
      } else {
        // Image is taller than canvas (relative to their widths)
        displayedHeight = canvasHeight;
        displayedWidth = displayedHeight * imageRatio;
        offsetX = (canvasWidth - displayedWidth) / 2;
        offsetY = 0;
      }
      
      // Calculate the scaling factor from canvas coordinates to actual image coordinates
      const scaleX = actualImageWidth / displayedWidth;
      const scaleY = actualImageHeight / displayedHeight;
      
      // Convert crop rectangle from canvas coordinates to actual image coordinates
      // Ensure we're using the canvas dimensions, not the DOM element dimensions
      const scaledX = Math.max(0, (cropRect.x - offsetX) * scaleX);
      const scaledY = Math.max(0, (cropRect.y - offsetY) * scaleY);
      
      // Ensure we don't exceed the actual image dimensions
      const scaledWidth = Math.min(
        actualImageWidth - scaledX,
        cropRect.width * scaleX
      );
      const scaledHeight = Math.min(
        actualImageHeight - scaledY,
        cropRect.height * scaleY
      );
      
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Could not get canvas context');
      }
      
      // Set canvas dimensions to the cropped area
      canvas.width = scaledWidth;
      canvas.height = scaledHeight;
      
      // Draw the cropped image onto the canvas
      ctx.drawImage(
        image,
        scaledX,
        scaledY,
        scaledWidth,
        scaledHeight,
        0,
        0,
        scaledWidth,
        scaledHeight
      );

      // Convert the canvas to a data URL
      const croppedImageBase64 = canvas.toDataURL('image/png');
      
      // Pass the cropped image back to the parent component
      onCropComplete(croppedImageBase64);
      
      // Close the dialog
      handleClose(); // This now directly calls onClose
      
      showNotification('success', 'Diagram cropped successfully');
    } catch (error) {
      console.error('Error creating cropped image:', error);
      showNotification('error', 'Failed to crop diagram');
    }
  }, [imageSource, cropRect, onCropComplete, handleClose, showNotification]);

  return (
    <motion.div
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }} // Standard overlay transition
    >
      <motion.div
        ref={dialogRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[800px] mx-4 border border-gray-200 dark:border-gray-700"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        {/* Header */}
        <div className="px-6 pt-5 rounded-t-lg">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Crop Diagram</h2>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Cropper */}
              <div className="relative w-full h-[400px] bg-gray-100 dark:bg-gray-700 rounded-md mb-6">
                {imageSource ? (
                  <div className="relative w-full h-full">
                    {/* The image */}
                    <img 
                      ref={imageRef}
                      src={imageSource} 
                      alt="Diagram" 
                      className="absolute top-0 left-0 w-full h-full object-contain"
                    />
                    
                    {/* Canvas overlay for drawing the crop rectangle */}
                    <canvas
                      ref={canvasRef}
                      width={800}
                      height={400}
                      className="absolute top-0 left-0 w-full h-full"
                      style={{ 
                        cursor: activeHandle ? 
                          getCursorForHandle(activeHandle) : 
                          (hoverHandle ? 
                            getCursorForHandle(hoverHandle) : 
                            'crosshair')
                      }}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={(e) => {
                        e.preventDefault();
                        handleMouseUp(e);
                      }}
                    />
                    
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">No diagram captured yet</p>
                    <button
                      onClick={captureReactFlowDiagram}
                      disabled={isCapturing}
                      className={`
                        px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2
                        ${isCapturing
                          ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                          : 'bg-primary-500 hover:bg-primary-600 text-white'
                        }
                      `}
                    >
                      {isCapturing ? 'Capturing...' : 'Capture Diagram'}
                    </button>
                  </div>
                )}
              </div>
              
              {/* Footer Actions */}
              <div className="flex justify-between mt-6">
                {imageSource ? (
                  <>
                    <div className="flex gap-2">
                      <button
                        onClick={captureReactFlowDiagram}
                        disabled={isCapturing}
                        className={`
                          px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2
                          ${isCapturing
                            ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                            : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                          }
                        `}
                      >
                        <RefreshCw className="h-4 w-4" />
                        Recapture
                      </button>
                      <button
                        onClick={async () => {
                          if (!imageSource) {
                            showNotification('error', 'No image to auto-crop');
                            return;
                          }
                          
                          try {
                            setIsCapturing(true); // Reuse the loading state
                            showNotification('info', 'Auto-cropping image...');
                            
                            // Apply auto-cropping
                            const croppedImage = await autoCropWhiteMargins(imageSource);
                            
                            // Pass the auto-cropped image back to the parent
                            onCropComplete(croppedImage);
                            
                            // Close the dialog
                            handleClose();
                            
                            showNotification('success', 'Image auto-cropped successfully');
                          } catch (error) {
                            console.error('Error auto-cropping image:', error);
                            showNotification('error', 'Failed to auto-crop image');
                          } finally {
                            setIsCapturing(false);
                          }
                        }}
                        disabled={isCapturing || !imageSource}
                        className={`
                          px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2
                          ${isCapturing || !imageSource
                            ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                            : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                          }
                        `}
                      >
                        <Scissors className="h-4 w-4" />
                        Auto-Crop
                      </button>
                    </div>
                    <button
                      onClick={createCroppedImage}
                      disabled={!cropRect}
                      className={`
                        px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2 text-white
                        ${!cropRect
                          ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                          : 'bg-primary-500 hover:bg-primary-600'
                        }
                      `}
                    >
                      <Check className="h-4 w-4" />
                      Apply Crop
                    </button>
                  </>
                ) : (
                  <div></div> // Empty div to maintain layout when no image
                )}
              </div>
            </div>
      </motion.div>
    </motion.div>
  );
}
